import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import { Calendar, Loader } from "lucide-react";
import { getFlexFieldValue } from "../../../redux/features/common/api";

interface WebFlexWorkTileProps {
    fieldCode: string;
    totalDays?: number;
    variant?: "remote-work" | "forfait-jours";
    progressColors?: {
        low: string;      // < 50%
        medium: string;   // 50-75%
        high: string;     // > 75%
    };
    loaderColor?: string;
    startDate?: string;
}

const WebFlexWorkTile: React.FC<WebFlexWorkTileProps> = ({
    fieldCode,
    totalDays = 60,
    variant = "remote-work",
    progressColors = {
        low: "bg-blue-500",
        medium: "bg-yellow-500",
        high: "bg-red-500",
    },
    loaderColor = "text-blue-500",
    startDate = "Jan 01, 2026",
}) => {
    const dispatch = useAppDispatch();
    const user = useSelector((state: RootState) => state.user.user);
    const flexFieldData = useSelector((state: RootState) => state.common.flexFieldValues[fieldCode]);
    const isLoading = useSelector((state: RootState) => state.common.flexFieldLoading[fieldCode]);

    useEffect(() => {
        // Fetch flex field data when component mounts
        if (user?.id && user.id !== -1) {
            dispatch(
                getFlexFieldValue({
                    field_code: fieldCode,
                    employee_id: user.id.toString(),
                })
            );
        }
    }, [dispatch, user?.id, fieldCode]);

    const daysUsed = parseFloat(flexFieldData?.field_value || "0");
    const percentage = (daysUsed / totalDays) * 100;
    const isForfaitJours = variant === "forfait-jours";
    const subLabel = isForfaitJours ? `${daysUsed} / ${totalDays} days worked this year` : "days used";

    // Determine color based on usage
    const getProgressColor = () => {
        if (percentage < 50) return progressColors.low;
        if (percentage < 75) return progressColors.medium;
        return progressColors.high;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-6">
                <Loader className={`w-6 h-6 ${loaderColor} animate-spin`} />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Days Counter */}
            <div className="text-center">
                <div className="text-3xl font-bold text-gray-900">
                    {isForfaitJours ? (
                        daysUsed
                    ) : (
                        <>
                            {daysUsed} <span className="text-gray-400">of</span> {totalDays}
                        </>
                    )}
                </div>
                <div className="text-sm text-gray-500 mt-1">{subLabel}</div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                    className={`h-2.5 rounded-full ${getProgressColor()} transition-all duration-300`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                ></div>
            </div>

            {/* Start Date */}
            {!isForfaitJours && (
                <div className="flex items-center justify-center text-sm text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span>Since {startDate}</span>
                </div>
            )}
        </div>
    );
};

export default WebFlexWorkTile;
